import { FC, useState, useEffect, useRef } from "react"
import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import Icon from "@mui/material/Icon"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import { MedicalRecordInfo } from "../TenantInfo"

export interface AlertHistoryEvent {
  dateTime: number
  description: string
}

export interface AlertHistoryProps {
  info: MedicalRecordInfo | null
  events: AlertHistoryEvent[]
}

// ロケール対応の日付フォーマット関数
const formatDateWithLocale = (timestamp: number, locale: string = "ja-JP"): string => {
  const date = new Date(timestamp * 1000)
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false // 24時間形式
  }).format(date)
}

export const AlertHistory: FC<AlertHistoryProps> = ({ info, events }) => {
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [visibleItems, setVisibleItems] = useState(events.slice(0, 5))
  const contentRef = useRef<HTMLDivElement | null>(null)

  // 初期表示データの設定
  useEffect(() => {
    let filteredItems = events

    if (startDate || endDate) {
      const start = startDate ? new Date(startDate).getTime() : -Infinity
      const end = endDate ? new Date(endDate).getTime() : Infinity

      filteredItems = events.filter((event) => {
        const date = event.dateTime * 1000
        return date >= start && date <= end
      })
    }

    filteredItems.sort((a, b) => b.dateTime - a.dateTime)
    setVisibleItems(filteredItems.slice(0, 5))
  }, [events])

  const handleSearch = () => {
    let filteredItems = events

    // フィルター処理
    if (startDate || endDate) {
      const start = startDate ? new Date(startDate).getTime() : -Infinity
      const end = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : Infinity

      filteredItems = events.filter((event) => {
        const date = event.dateTime * 1000 // 秒をミリ秒に変換
        return date >= start && date <= end
      })
    }

    // 古い順にソート
    filteredItems.sort((a, b) => b.dateTime - a.dateTime)

    // 初期表示件数を設定
    setVisibleItems(filteredItems.slice(0, 5))
  }

  // スクロール時の処理
  const handleScroll = () => {
    const contentEl = contentRef.current
    if (!contentEl) return

    // スクロールが末尾に近づいたら追加読み込み
    if (contentEl.scrollTop + contentEl.clientHeight >= contentEl.scrollHeight - 10) {
      setVisibleItems((prevVisibleItems) => {
        const nextIndex = prevVisibleItems.length
        const nextItems = events.slice(nextIndex, nextIndex + 5) // さらに5件取得
        return [...prevVisibleItems, ...nextItems]
      })
    }
  }

  // スクロールイベントを登録
  useEffect(() => {
    setVisibleItems((prevVisibleItems) => {
      const nextIndex = prevVisibleItems.length
      const nextItems = events.slice(nextIndex, nextIndex + 5) // さらに5件取得
      return [...prevVisibleItems, ...nextItems]
    })

    const contentEl = contentRef.current
    if (contentEl) {
      contentEl.addEventListener("scroll", handleScroll)
    }
    return () => {
      if (contentEl) {
        contentEl.removeEventListener("scroll", handleScroll)
      }
    }
  }, [events])

  // CSVダウンロード処理
  const handleDownloadCSV = () => {
    let headers = []
    const auth = localStorage.getItem("account_type")
    if (auth == "estate") {
      headers = [
        "施設ID",
        "施設名",
        "施設住所",
        "施設電話番号",
        "居室番号",
        "入居者名",
        "年齢",
        "電話番号",
        "日付時刻",
        "機器エラー内容"
      ]
    } else {
      headers = ["施設ID", "施設名", "居室番号", "入居者イニシャル", "日付時刻", "機器エラー内容"]
    }

    const csvContent = [
      headers.join(","),
      ...visibleItems.map((item) => {
        const formattedDate = formatDateWithLocale(item.dateTime)
        const alertMessage = item.description

        const facilityId = info?.facility_id || "F12345"
        const facilityName = info?.facility_name || "サンプル施設"
        const facilityAddress = info?.address || "東京都新宿区1-1-1"
        const facilityPhone = info?.phone || "03-1234-5678"
        const roomNumber = info?.room_no || "101"
        const residentName = info?.name || "山田 太郎"
        const age = info?.age || 80
        const phoneNumber = info?.phone || "080-1234-5678"

        if (auth == "estate") {
          return [
            facilityId,
            facilityName,
            facilityAddress,
            facilityPhone,
            roomNumber,
            residentName,
            age,
            phoneNumber,
            formattedDate,
            alertMessage
          ].join(",")
        } else {
          return [facilityId, facilityName, roomNumber, residentName, formattedDate, alertMessage].join(",")
        }
      })
    ].join("\n")

    const utf8Bom = "\uFEFF"
    const blob = new Blob([utf8Bom + csvContent], { type: "text/csv;charset=utf-8;" })

    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "medical_record_history.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card>
      <MDBox pt={3} px={3}>
        <MDTypography variant="h4" fontWeight="medium">
          機器エラー履歴
        </MDTypography>
        <MDBox display="flex" flexWrap="wrap" alignItems="left" mb={3} gap={3}>
          <MDInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            sx={{ width: "160px" }}
          />
          〜
          <MDInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ width: "160px" }} />
          <MDButton variant="gradient" color="info" size="medium" onClick={handleSearch}>
            <Icon>search</Icon>&nbsp; 検索
          </MDButton>
          <MDButton variant="outlined" color="info" size="medium" onClick={handleDownloadCSV}>
            <Icon>download</Icon>&nbsp; CSV
          </MDButton>
        </MDBox>
      </MDBox>
      <MDBox>
        <TableContainer ref={contentRef} sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "300px" }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", display: "table-header-group", zIndex: 3 }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold",
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                <TableCell align="left">日時</TableCell>
                <TableCell align="left">内容</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal"
                    }
                  }}
                >
                  <TableCell align="left">{formatDateWithLocale(row.dateTime)}</TableCell>
                  <TableCell align="left">{row.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    </Card>
  )
}
